/**
 * Error Handler Utilities for Smart Storage Checker
 * Provides centralized error handling and user-friendly error messages
 */

// Error types
export const ERROR_TYPES = {
  API_ERROR: 'API_ERROR',
  NETWORK_ERROR: 'NETWORK_ERROR',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  FILE_ERROR: 'FILE_ERROR',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
};

// HTTP Status Code mapping
export const HTTP_STATUS_MESSAGES = {
  400: 'Ungültige Anfrage. Bitte überprüfen Sie Ihre Eingaben.',
  401: 'Nicht autorisiert. Bitte melden Sie sich an.',
  403: 'Zugriff verweigert. Sie haben keine Berechtigung für diese Aktion.',
  404: 'Die angeforderte Ressource wurde nicht gefunden.',
  413: 'Die Datei ist zu groß. Maximale Dateigröße: 50MB.',
  422: 'Die Daten konnten nicht verarbeitet werden. Bitte überprüfen Sie das Dateiformat.',
  429: 'Zu viele Anfragen. Bitte versuchen Sie es später erneut.',
  500: 'Ein Serverfehler ist aufgetreten. Bitte versuchen Sie es später erneut.',
  502: 'Der Server ist vorübergehend nicht verfügbar.',
  503: 'Der Service ist vorübergehend nicht verfügbar.',
};

// File upload specific errors
export const FILE_ERROR_MESSAGES = {
  'file-too-large': 'Die Datei ist zu groß. Maximale Größe: 50MB.',
  'file-invalid-type': 'Ungültiger Dateityp. Nur CSV-Dateien sind erlaubt.',
  'file-corrupt': 'Die Datei ist beschädigt oder kann nicht gelesen werden.',
  'file-empty': 'Die Datei ist leer oder enthält keine gültigen Daten.',
  'csv-invalid-format': 'Das CSV-Format ist ungültig. Bitte überprüfen Sie die Spaltenstruktur.',
  'csv-missing-columns': 'Erforderliche Spalten fehlen (timestamp, current_A, voltage_V, power_W).',
  'csv-insufficient-data': 'Nicht genügend Daten. Mindestens 24 Stunden Messdaten erforderlich.',
};

// Analysis specific errors
export const ANALYSIS_ERROR_MESSAGES = {
  'analysis-failed': 'Die Analyse konnte nicht durchgeführt werden.',
  'analysis-timeout': 'Die Analyse hat zu lange gedauert. Bitte versuchen Sie es erneut.',
  'analysis-insufficient-data': 'Nicht genügend Daten für eine aussagekräftige Analyse.',
  'analysis-invalid-data': 'Die Daten enthalten ungültige Werte.',
};

/**
 * Main error handler function
 * @param {Object} error - Error object from API client or other sources
 * @returns {Object} Formatted error object with user-friendly message
 */
export function handleError(error) {
  console.error('Error Handler:', error);

  // If error is already formatted by API client
  if (error.type) {
    return formatError(error);
  }

  // Handle different error types
  if (error.response) {
    // HTTP response errors
    return handleHttpError(error.response);
  } else if (error.request) {
    // Network errors
    return handleNetworkError(error);
  } else if (error.code) {
    // File/browser specific errors
    return handleFileError(error);
  } else {
    // Generic errors
    return handleGenericError(error);
  }
}

/**
 * Format error object for display
 * @param {Object} error - Error object
 * @returns {Object} Formatted error
 */
function formatError(error) {
  let userMessage = '';
  let severity = 'error';

  switch (error.type) {
    case ERROR_TYPES.API_ERROR:
      userMessage = getHttpStatusMessage(error.status) || error.message;
      severity = error.status < 500 ? 'warning' : 'error';
      break;

    case ERROR_TYPES.NETWORK_ERROR:
      userMessage = 'Verbindungsfehler. Bitte überprüfen Sie Ihre Internetverbindung.';
      severity = 'error';
      break;

    case ERROR_TYPES.FILE_ERROR:
      userMessage = getFileErrorMessage(error.code) || error.message;
      severity = 'warning';
      break;

    case ERROR_TYPES.VALIDATION_ERROR:
      userMessage = error.message || 'Eingabe ungültig. Bitte überprüfen Sie Ihre Daten.';
      severity = 'warning';
      break;

    default:
      userMessage = error.message || 'Ein unbekannter Fehler ist aufgetreten.';
      severity = 'error';
  }

  return {
    type: error.type,
    message: userMessage,
    severity,
    details: error.details,
    timestamp: new Date().toISOString(),
    originalError: error.originalError,
  };
}

/**
 * Handle HTTP response errors
 * @param {Object} response - HTTP response object
 * @returns {Object} Formatted error
 */
function handleHttpError(response) {
  const { status, data } = response;

  return {
    type: ERROR_TYPES.API_ERROR,
    status,
    message: data?.message || getHttpStatusMessage(status) || 'HTTP Fehler',
    details: data?.details,
    severity: status < 500 ? 'warning' : 'error',
    timestamp: new Date().toISOString(),
  };
}

/**
 * Handle network errors
 * @param {Object} error - Network error object
 * @returns {Object} Formatted error
 */
function handleNetworkError(error) {
  return {
    type: ERROR_TYPES.NETWORK_ERROR,
    message: 'Netzwerkfehler. Bitte überprüfen Sie Ihre Internetverbindung.',
    severity: 'error',
    details: 'Der Server ist möglicherweise nicht erreichbar.',
    timestamp: new Date().toISOString(),
    originalError: error,
  };
}

/**
 * Handle file-related errors
 * @param {Object} error - File error object
 * @returns {Object} Formatted error
 */
function handleFileError(error) {
  return {
    type: ERROR_TYPES.FILE_ERROR,
    code: error.code,
    message: getFileErrorMessage(error.code) || error.message,
    severity: 'warning',
    timestamp: new Date().toISOString(),
    originalError: error,
  };
}

/**
 * Handle generic errors
 * @param {Object} error - Generic error object
 * @returns {Object} Formatted error
 */
function handleGenericError(error) {
  return {
    type: ERROR_TYPES.UNKNOWN_ERROR,
    message: error.message || 'Ein unbekannter Fehler ist aufgetreten.',
    severity: 'error',
    timestamp: new Date().toISOString(),
    originalError: error,
  };
}

/**
 * Get user-friendly message for HTTP status codes
 * @param {number} status - HTTP status code
 * @returns {string} User-friendly message
 */
function getHttpStatusMessage(status) {
  return HTTP_STATUS_MESSAGES[status] || `HTTP ${status} Fehler`;
}

/**
 * Get user-friendly message for file errors
 * @param {string} code - Error code
 * @returns {string} User-friendly message
 */
function getFileErrorMessage(code) {
  return FILE_ERROR_MESSAGES[code] || ANALYSIS_ERROR_MESSAGES[code];
}

/**
 * Create validation error
 * @param {string} field - Field name
 * @param {string} message - Error message
 * @returns {Object} Validation error
 */
export function createValidationError(field, message) {
  return {
    type: ERROR_TYPES.VALIDATION_ERROR,
    field,
    message,
    severity: 'warning',
    timestamp: new Date().toISOString(),
  };
}

/**
 * Create file error
 * @param {string} code - Error code
 * @param {string} filename - File name
 * @returns {Object} File error
 */
export function createFileError(code, filename = '') {
  return {
    type: ERROR_TYPES.FILE_ERROR,
    code,
    filename,
    message: getFileErrorMessage(code),
    severity: 'warning',
    timestamp: new Date().toISOString(),
  };
}

/**
 * Check if error is retryable
 * @param {Object} error - Error object
 * @returns {boolean} True if error is retryable
 */
export function isRetryableError(error) {
  if (error.type === ERROR_TYPES.NETWORK_ERROR) return true;
  if (error.type === ERROR_TYPES.API_ERROR && error.status >= 500) return true;
  if (error.type === ERROR_TYPES.API_ERROR && error.status === 429) return true;
  return false;
}

/**
 * Get retry delay based on error type
 * @param {Object} error - Error object
 * @param {number} attempt - Retry attempt number
 * @returns {number} Delay in milliseconds
 */
export function getRetryDelay(error, attempt = 1) {
  if (!isRetryableError(error)) return 0;

  // Exponential backoff with jitter
  const baseDelay = 1000; // 1 second
  const maxDelay = 30000; // 30 seconds
  const exponentialDelay = baseDelay * Math.pow(2, attempt - 1);
  const jitter = Math.random() * 1000; // Add randomness

  return Math.min(exponentialDelay + jitter, maxDelay);
}

/**
 * Log error for debugging and monitoring
 * @param {Object} error - Error object
 * @param {Object} context - Additional context
 */
export function logError(error, context = {}) {
  const errorLog = {
    timestamp: new Date().toISOString(),
    error: {
      type: error.type,
      message: error.message,
      status: error.status,
      code: error.code,
    },
    context,
    userAgent: navigator.userAgent,
    url: window.location.href,
  };

  console.error('Error Log:', errorLog);

  // In production, you might want to send this to an error tracking service
  // Example: sendToErrorTracking(errorLog);
}

// Default export
export default {
  handleError,
  createValidationError,
  createFileError,
  isRetryableError,
  getRetryDelay,
  logError,
  ERROR_TYPES,
};